import { useMemo } from 'react';
import { useGT } from 'gt-react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { NavButtonToolResult } from './NavButtonToolResult';

// Mirrors the backend list_my_tasks result. Local (single consumer).
const resultSchema = z.object({
  tasks: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: z.string(),
      priority: z.string(),
      dueDate: z.string().nullable(),
    }),
  ),
});

const PRIORITY_CLASS: Record<string, string> = {
  urgent: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  medium: 'text-muted-foreground',
  low: 'text-muted-foreground',
};

interface TasksToolResultProps {
  /** The list_my_tasks tool result (data lives in the result, not args). */
  result: unknown;
}

/**
 * Renders the AI's `list_my_tasks` result as a compact list; each task links
 * into the Inbox tasks tab. The model summarizes in prose.
 */
export function TasksToolResult({ result }: TasksToolResultProps) {
  const gt = useGT();
  const parsed = useMemo(() => resultSchema.safeParse(result), [result]);

  if (!parsed.success || parsed.data.tasks.length === 0) return null;

  return (
    <Card>
      <ul className="flex flex-col gap-2.5">
        {parsed.data.tasks.map((t) => (
          <li key={t.id} className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-card-foreground text-sm">{t.title}</span>
              <span
                className={cn(
                  'text-xs capitalize',
                  PRIORITY_CLASS[t.priority] ?? 'text-muted-foreground',
                )}
              >
                {`${t.priority} · ${t.status.replace('_', ' ')}`}
              </span>
            </div>
            <NavButtonToolResult
              to={`/inbox?tab=tasks&taskId=${t.id}`}
              label={gt('Open', { context: 'button that opens a task' })}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}
